/**
 * CashFlowSideCharts Component
 *
 * Single panel containing two charts, stacked vertically (like the reference design):
 *  1. Donut chart: Revenue vs Cost realized (%)
 *  2. Horizontal bars: Cost breakdown by major category (Budget vs Realized)
 */

import React, { useMemo } from 'react';
import {
    Box,
    Center,
    Divider,
    HStack,
    Skeleton,
    Text,
    VStack,
} from '@chakra-ui/react';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
} from 'recharts';
import { CashFlowSummary, CashFlowDetailRow } from '../types';
import { formatCompact, formatNumber } from '../utils';

const REV_COLOR = '#1E8A4E';
const COST_COLOR = '#BD4A30';
const BUDGET_COLOR = '#D2CCBA';
const REALIZED_COLOR = '#157A43';

interface CashFlowSideChartsProps {
    summary: CashFlowSummary | null;
    detail: CashFlowDetailRow[];
    loading: boolean;
}

// Cost breakdown codes mapped to short labels (matches FRD categories)
const COST_CODE_LABELS: Record<string, string> = {
    '1.2.1': 'Payroll',
    '1.2.2': 'Ops Langsung',
    '1.2.3': 'Ops HO',
    '1.2.4': 'Ops Site',
    '2.2': 'Non-Ops',
    '3.2': 'Pendanaan',
};

const COST_BREAKDOWN_CODES = Object.keys(COST_CODE_LABELS);

const DonutTooltip: React.FC<any> = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box bg="white" p={2} borderRadius="md" boxShadow="md" border="1px" borderColor="gray.200">
            <Text fontSize="xs" fontWeight="semibold">{payload[0].name}</Text>
            <Text fontSize="xs" fontFamily="mono" color={payload[0].payload.fill}>
                {formatNumber(payload[0].value)}
            </Text>
        </Box>
    );
};

// Custom horizontal bar row — mirrors the reference `.hbar-row` layout
const HBarRow: React.FC<{ name: string; budget: number; realized: number; max: number }> = ({
    name,
    budget,
    realized,
    max,
}) => {
    const budgetPct = max > 0 ? (budget / max) * 100 : 0;
    const realizedPct = max > 0 ? (realized / max) * 100 : 0;

    return (
        <HStack spacing={2} align="center" w="100%">
            <Text
                fontSize="10px"
                color="gray.500"
                fontWeight="500"
                textAlign="right"
                minW="72px"
                flexShrink={0}
                noOfLines={1}
            >
                {name}
            </Text>
            <Box flex={1} position="relative" h="14px" bg="gray.100" borderRadius="sm" overflow="hidden">
                {/* Budget bar (background, lighter) */}
                <Box
                    position="absolute"
                    left={0}
                    top={0}
                    bottom={0}
                    w={`${budgetPct}%`}
                    bg={BUDGET_COLOR}
                    borderRadius="sm"
                />
                {/* Realized bar (foreground, solid green) */}
                <Box
                    position="absolute"
                    left={0}
                    top={0}
                    bottom={0}
                    w={`${realizedPct}%`}
                    bg={REALIZED_COLOR}
                    borderRadius="sm"
                />
            </Box>
            <Text fontSize="10px" fontFamily="mono" fontWeight="600" color="gray.700" minW="40px" textAlign="right" flexShrink={0}>
                {formatCompact(realized)}
            </Text>
        </HStack>
    );
};

const CashFlowSideCharts: React.FC<CashFlowSideChartsProps> = ({ summary, detail, loading }) => {
    const donutData = useMemo(() => {
        if (!summary) return [];
        const rev = summary.total_revenue_realized;
        const cost = summary.total_cost_realized;
        if (rev + cost === 0) return [];
        return [
            { name: 'Revenue', value: rev, fill: REV_COLOR },
            { name: 'Cost', value: cost, fill: COST_COLOR },
        ];
    }, [summary]);

    const revPct = useMemo(() => {
        if (!summary) return 0;
        const total = summary.total_revenue_realized + summary.total_cost_realized;
        return total > 0 ? (summary.total_revenue_realized / total) * 100 : 0;
    }, [summary]);

    const barRows = useMemo(() => {
        return COST_BREAKDOWN_CODES
            .map((code) => {
                const row = detail.find((d) => d.code === code);
                if (!row) return null;
                return { name: COST_CODE_LABELS[code], budget: row.budget, realized: row.realized };
            })
            .filter((r): r is { name: string; budget: number; realized: number } => r !== null);
    }, [detail]);

    const barMax = useMemo(
        () => Math.max(...barRows.map((r) => Math.max(r.budget, r.realized)), 1),
        [barRows],
    );

    if (loading) {
        return <Skeleton height="380px" borderRadius="xl" />;
    }

    return (
        <Box bg="white" p={4} borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
            {/* Donut: Revenue vs Cost */}
            <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>Revenue vs Cost</Text>
            {donutData.length === 0 ? (
                <Center h="140px">
                    <Text color="gray.400" fontSize="sm">Tidak ada data</Text>
                </Center>
            ) : (
                <VStack spacing={2}>
                    <Box position="relative" w="100%">
                        <ResponsiveContainer width="100%" height={150}>
                            <PieChart>
                                <Pie
                                    data={donutData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={48}
                                    outerRadius={68}
                                    dataKey="value"
                                    startAngle={90}
                                    endAngle={-270}
                                    strokeWidth={0}
                                >
                                    {donutData.map((entry, i) => (
                                        <Cell key={i} fill={entry.fill} />
                                    ))}
                                </Pie>
                                <Tooltip content={<DonutTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                        {/* Center label */}
                        <Box
                            position="absolute"
                            top="50%"
                            left="50%"
                            transform="translate(-50%, -50%)"
                            textAlign="center"
                            pointerEvents="none"
                        >
                            <Text fontSize="xl" fontWeight="bold" fontFamily="mono" color={REV_COLOR} lineHeight={1}>
                                {revPct.toFixed(0)}%
                            </Text>
                            <Text fontSize="9px" color="gray.400" textTransform="uppercase" letterSpacing="wide">
                                Revenue
                            </Text>
                        </Box>
                    </Box>
                    <HStack spacing={4} justify="center">
                        <HStack spacing={1}>
                            <Box w="10px" h="10px" borderRadius="2px" bg={REV_COLOR} flexShrink={0} />
                            <Text fontSize="xs" color="gray.500">Revenue</Text>
                        </HStack>
                        <HStack spacing={1}>
                            <Box w="10px" h="10px" borderRadius="2px" bg={COST_COLOR} flexShrink={0} />
                            <Text fontSize="xs" color="gray.500">Cost</Text>
                        </HStack>
                    </HStack>
                </VStack>
            )}

            <Divider my={4} borderColor="gray.100" />

            {/* Horizontal bars: Cost Breakdown */}
            <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>Breakdown Biaya</Text>
            {barRows.length === 0 ? (
                <Center h="80px">
                    <Text color="gray.400" fontSize="sm">Tidak ada data</Text>
                </Center>
            ) : (
                <VStack spacing={2} align="stretch">
                    {barRows.map((row) => (
                        <HBarRow
                            key={row.name}
                            name={row.name}
                            budget={row.budget}
                            realized={row.realized}
                            max={barMax}
                        />
                    ))}
                    <HStack spacing={3} pt={1}>
                        <HStack spacing={1}>
                            <Box w="10px" h="10px" borderRadius="2px" bg={BUDGET_COLOR} flexShrink={0} />
                            <Text fontSize="10px" color="gray.500">Budget</Text>
                        </HStack>
                        <HStack spacing={1}>
                            <Box w="10px" h="10px" borderRadius="2px" bg={REALIZED_COLOR} flexShrink={0} />
                            <Text fontSize="10px" color="gray.500">Realized</Text>
                        </HStack>
                    </HStack>
                </VStack>
            )}
        </Box>
    );
};

export default CashFlowSideCharts;
